import { DownloadOutlined, FileZipOutlined } from '@ant-design/icons';
import { Button, Space, Tooltip, Table } from 'antd';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StatePieceStatus } from '../../../../enums';
import { IDocument } from '../../../../interfaces/documents/data/IDocument';
import { IInvoiceDocumentTableRow } from '../../../../interfaces/documents/IInvoiceDocumentTableRow';
import { ContentHeader } from '../../../../shared/components/appSkeleton/contentHeader/ContentHeader';
import { SearchBar } from '../../../../shared/components/filters/SearchBar';
import { HighlightedText } from '../../../../shared/components/highlightedText/HighlightedText';
import { Unauthorized } from '../../../../shared/components/unauthorized/Unauthorized';
import { calculateOverviewTableHeight } from '../../../../shared/helpers/TableHeightHelper';
import { AppDispatch, RootState } from '../../../../store';
import { resetState } from '../../../../store/beneficiarySlice/BeneficiarySlice';
import { downloadDocument, downloadDocuments, fetchAllInvoiceDocuments } from '../../../../store/documentSlice/DocumentActions';
import { DocumentTabNavigation } from '../../documentTabNavigation/DocumentTabNavigation';
import { RowSelection } from '../../../../interfaces';

export const InvoiceDocumentsOverview = () => {
    const dispatch = useDispatch<AppDispatch>();
    const documentSlice = useSelector((state: RootState) => state.documentSlice);
    const appSlice = useSelector((state: RootState) => state.appSlice);
    const [tableRenderHeight, setTableRenderHeight] = useState<number>();
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedTableRecords, setSelectedTabelRecords] = useState<IInvoiceDocumentTableRow[]>([]);
    const [tableRowSelection, setTableRowSelection] = useState<RowSelection<IInvoiceDocumentTableRow>>({});

    const [filtering, setFiltering] = useState<{
        pagination?: TablePaginationConfig;
        filters?: Record<string, FilterValue | null>;
        _sorter?: SorterResult<IInvoiceDocumentTableRow> | SorterResult<IInvoiceDocumentTableRow>[];
        searchQuery?: string;
    }>({});

    const columns: ColumnsType<IInvoiceDocumentTableRow> = [
        {
            title: 'Document Id',
            dataIndex: 'document',
            key: 'document',
            render: (document: IDocument) => (
                <HighlightedText text={document.id.toString() || ''} textToHighlight={filtering.searchQuery} />
            ),
        },

        {
            title: 'File',
            dataIndex: 'subject',
            key: 'subject',
            sorter: true,
            render: (_, record) => <HighlightedText text={record.document.fileName || ''} textToHighlight={filtering.searchQuery} />,
        },
        {
            title: 'Created',
            dataIndex: 'created',
            key: 'created',
            sorter: true,
            render: (text: string) => <>{dayjs(text).format('DD-MM-YYYY - HH:mm')}</>,
        },
        {
            title: 'Created by',
            dataIndex: 'createdByUser',
            key: 'createdByUser',
            render: (_, record) => (
                <>
                    {record.createdBy && record.createdBy.initials && (
                        <Tooltip title={record.createdBy.name}>
                            <HighlightedText text={record.createdBy.initials || ''} textToHighlight={filtering.searchQuery} />
                        </Tooltip>
                    )}
                </>
            ),
        },
        {
            title: '',
            dataIndex: 'actions',
            key: 'actions',
            width: 65,
            fixed: 'right',
            render: (_, record) => (
                <Space size="middle">
                    <Tooltip title="Download">
                        <Button
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={() => {
                                dispatch(downloadDocument({ id: record.document.id, fileName: record.document.fileName || '' }));
                            }}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    const rowSelection = {
        selectedRowKeys,
        onChange: (selectedRowKeys: React.Key[], selectedRows: IInvoiceDocumentTableRow[]) => {
            // #region determine the new tableRowSelection and selection keys
            const currentPage = filtering.pagination?.current || 1;
            let newSelectedTableRecordsObj = { ...tableRowSelection };
            if (selectedRows.length === 0) {
                delete newSelectedTableRecordsObj[currentPage];
            } else {
                newSelectedTableRecordsObj[currentPage] = selectedRows;
            }
            setTableRowSelection(newSelectedTableRecordsObj);

            let accumulatedSelectedRecords = [] as IInvoiceDocumentTableRow[];
            Object.keys(newSelectedTableRecordsObj).forEach((key: string) => {
                accumulatedSelectedRecords = [...accumulatedSelectedRecords, ...newSelectedTableRecordsObj[key]];
            });
            setSelectedTabelRecords(accumulatedSelectedRecords);

            const accumulatedSelectedKeys = accumulatedSelectedRecords.map((r) => r.id.toString());
            setSelectedRowKeys(accumulatedSelectedKeys);
            // #endregion
        },
    };

    const fetchInvoices = () => {
        const sorter = filtering._sorter as SorterResult<IInvoiceDocumentTableRow[]>;
        dispatch(
            fetchAllInvoiceDocuments({
                pageSize: filtering.pagination?.pageSize,
                pageNumber: filtering.pagination?.current,
                sorter,
                searchQuery: filtering.searchQuery,
            })
        );
    };

    useEffect(() => {
        let renderTableHeight = calculateOverviewTableHeight();
        setTableRenderHeight(renderTableHeight);

        return () => {
            dispatch(resetState());
        };
    }, []);

    useEffect(() => {
        fetchInvoices();
    }, [filtering]);

    if (appSlice.auth.validationCompleted && !appSlice.auth.documents.invoices.view) {
        return <Unauthorized />;
    }

    return (
        <div className="app-page">
            <ContentHeader
                title="Invoices"
                tabNavigation={<DocumentTabNavigation />}
                hideBackButton
                breadcrumbs={[
                    {
                        breadcrumbName: 'Documents',
                        path: '/documents',
                    },
                    {
                        breadcrumbName: 'Invoices',
                        path: '/documents/invoices',
                    },
                ]}
            >
                <Space>
                    {selectedTableRecords.length > 0 && (
                        <Tooltip title={`Download selected invoices as a ZIP-file.`}>
                            <Button
                                icon={<FileZipOutlined />}
                                onClick={() => dispatch(downloadDocuments(selectedTableRecords.map((r) => r.document.id)))}
                                loading={documentSlice.invoiceDocuments.documentsDownloadStatus === StatePieceStatus.IsFetching}
                            >
                                Download as ZIP
                            </Button>
                        </Tooltip>
                    )}
                </Space>
            </ContentHeader>

            <div className="page-content">
                <SearchBar
                    loading={documentSlice.invoiceDocuments.fetchStatus === StatePieceStatus.IsFetching}
                    placeholder="Search in invoices..."
                    onSearch={(value) => {
                        setFiltering({ ...filtering, searchQuery: value });
                    }}
                />

                <Table
                    size="small"
                    bordered={true}
                    scroll={{ y: tableRenderHeight }}
                    rowSelection={{
                        type: 'checkbox',
                        ...rowSelection,
                    }}
                    columns={columns}
                    loading={documentSlice.invoiceDocuments.fetchStatus === StatePieceStatus.IsFetching}
                    dataSource={documentSlice.invoiceDocuments.data.items.map((i) => {
                        return { ...i, key: i.id.toString() } as IInvoiceDocumentTableRow;
                    })}
                    pagination={{
                        current: documentSlice.invoiceDocuments.data.paging.pageNumber,
                        pageSize: documentSlice.invoiceDocuments.data.paging.pageSize,
                        total: documentSlice.invoiceDocuments.data.paging.totalItems,
                        showSizeChanger: true,
                    }}
                    onChange={(pagination, filters, _sorter) => {
                        setFiltering({ ...filtering, pagination, filters, _sorter: _sorter });
                    }}
                />
            </div>
        </div>
    );
};
